import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2,
  Shield,
  Mail,
  Phone,
  Calendar,
  UserCog
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminsPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/users/admins`);
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Error al cargar administradores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, {
        ...formData,
        user_type: 'admin'
      });
      toast.success(t('savedSuccessfully'));
      setShowCreateModal(false);
      resetForm();
      fetchAdmins();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      if (!updateData.password) delete updateData.password;
      
      await axios.put(`${API}/users/${selectedAdmin.id}`, updateData);
      toast.success(t('savedSuccessfully'));
      setShowProfileModal(false);
      setIsEditing(false);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    if (selectedAdmin.id === user?.id) {
      toast.error('No puedes eliminar tu propia cuenta');
      setShowDeleteDialog(false);
      return;
    }
    
    try {
      await axios.delete(`${API}/users/${selectedAdmin.id}`);
      toast.success(t('deletedSuccessfully'));
      setShowDeleteDialog(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openProfile = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      phone: admin.phone || '',
      password: ''
    });
    setIsEditing(false);
    setShowProfileModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', password: '' });
  };

  const filteredAdmins = admins.filter(a =>
    a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admins-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Administradores
          </h1>
          <p className="text-gray-600 mt-1">Gestión de usuarios administradores</p>
        </div>
        <Button 
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="bg-[#467322] hover:bg-[#3A601C] text-white"
          data-testid="create-admin-btn"
        >
          <Plus size={20} className="mr-2" />
          Nuevo Administrador
        </Button>
      </div>

      {/* Stats Card */}
      <Card className="bg-[#467322] border-none">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/20 rounded-lg">
                <Shield className="text-white" size={32} />
              </div>
              <div>
                <p className="text-white/80 text-sm">Total Administradores</p>
                <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {admins.length}
                </p>
              </div>
            </div>
            <div className="hidden sm:block text-right">
              <p className="text-white/80 text-sm">Usuarios con acceso total</p>
              <p className="text-white/60 text-xs mt-1">Pueden gestionar toda la plataforma</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="bg-white border border-[#467322]/20">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
            <Input
              placeholder="Buscar administradores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#467322]/30 focus:border-[#467322]"
              data-testid="search-admins"
            />
          </div>
        </CardContent>
      </Card>

      {/* Admins Table */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <UserCog size={24} />
            Lista de Administradores ({filteredAdmins.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredAdmins.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Shield size={48} className="mx-auto mb-4 opacity-50" />
              <p>No hay administradores registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-striped">
                <TableHeader>
                  <TableRow className="bg-[#467322]">
                    <TableHead className="text-white font-semibold">{t('name')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('email')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('phone')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('createdAt')}</TableHead>
                    <TableHead className="text-white font-semibold">Estado</TableHead>
                    <TableHead className="text-white font-semibold text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAdmins.map((admin) => (
                    <TableRow key={admin.id} className="hover:bg-[#F2F7F0]">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {admin.name}
                          {admin.id === user?.id && (
                            <Badge className="bg-[#467322]/10 text-[#467322] border-[#467322]/20 text-xs">
                              Tú
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{admin.email}</TableCell>
                      <TableCell>{admin.phone || '-'}</TableCell>
                      <TableCell>
                        {new Date(admin.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          Activo
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openProfile(admin)}
                            className="text-[#467322] hover:bg-[#F2F7F0]"
                            data-testid={`view-admin-${admin.id}`}
                          >
                            <Eye size={16} />
                          </Button>
                          {admin.id !== user?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedAdmin(admin);
                                setShowDeleteDialog(true);
                              }}
                              className="text-red-500 hover:bg-red-50"
                              data-testid={`delete-admin-${admin.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Shield size={24} />
              Nuevo Administrador
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-black">{t('name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Nombre completo"
                className="border-[#467322]/30 focus:border-[#467322]"
                data-testid="input-admin-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-black">{t('email')} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="correo@ejemplo.com"
                className="border-[#467322]/30 focus:border-[#467322]"
                data-testid="input-admin-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-black">{t('phone')}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1234567890"
                className="border-[#467322]/30 focus:border-[#467322]"
                data-testid="input-admin-phone"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-black">{t('password')} *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="Mínimo 8 caracteres"
                className="border-[#467322]/30 focus:border-[#467322]"
                data-testid="input-admin-password"
              />
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-700 text-sm flex items-start gap-2">
                <Shield size={16} className="mt-0.5 flex-shrink-0" />
                Este usuario tendrá acceso completo a toda la plataforma y podrá gestionar todos los módulos.
              </p>
            </div>
            
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-[#467322] text-[#467322]"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-[#467322] hover:bg-[#3A601C] text-white"
                data-testid="save-admin-btn"
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Profile/Edit Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Shield size={24} />
              {isEditing ? 'Editar Administrador' : 'Perfil de Administrador'}
            </DialogTitle>
          </DialogHeader>
          
          {isEditing ? (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-black">{t('name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="border-[#467322]/30 focus:border-[#467322]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-black">{t('email')} *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="border-[#467322]/30 focus:border-[#467322]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-black">{t('phone')}</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="border-[#467322]/30 focus:border-[#467322]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-black">{t('password')} (dejar vacío para mantener)</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Nueva contraseña"
                  className="border-[#467322]/30 focus:border-[#467322]"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  className="border-[#467322] text-[#467322]"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className="bg-[#467322] hover:bg-[#3A601C] text-white"
                >
                  {t('save')}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-[#F2F7F0] rounded-lg">
                <Shield className="text-[#467322]" size={20} />
                <div>
                  <p className="text-sm text-gray-500">{t('name')}</p>
                  <p className="font-semibold text-black">{selectedAdmin?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-[#F2F7F0] rounded-lg">
                <Mail className="text-[#467322]" size={20} />
                <div>
                  <p className="text-sm text-gray-500">{t('email')}</p>
                  <p className="font-semibold text-black">{selectedAdmin?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-[#F2F7F0] rounded-lg">
                <Phone className="text-[#467322]" size={20} />
                <div>
                  <p className="text-sm text-gray-500">{t('phone')}</p>
                  <p className="font-semibold text-black">{selectedAdmin?.phone || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-[#F2F7F0] rounded-lg">
                <Calendar className="text-[#467322]" size={20} />
                <div>
                  <p className="text-sm text-gray-500">{t('createdAt')}</p>
                  <p className="font-semibold text-black">
                    {selectedAdmin?.created_at && new Date(selectedAdmin.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <div className="p-3 bg-[#467322]/10 border border-[#467322]/20 rounded-lg">
                <p className="text-[#467322] text-sm flex items-center gap-2">
                  <Shield size={16} />
                  Rol: Administrador con acceso completo
                </p>
              </div>
              
              <DialogFooter>
                <Button
                  onClick={() => setIsEditing(true)}
                  className="bg-[#467322] hover:bg-[#3A601C] text-white w-full"
                  data-testid="edit-admin-btn"
                >
                  <Edit size={16} className="mr-2" />
                  {t('edit')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white border border-[#467322]/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#467322]">{t('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar al administrador <strong>{selectedAdmin?.name}</strong>? 
              Esta acción no se puede deshacer y el usuario perderá todo acceso a la plataforma.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#467322] text-[#467322]">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminsPage;
